import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Phase 3.A — manual open / close button for a gate.
 *
 * Routes:
 *   - apiBase="customer" → POST /api/customer/gates/<id>/commands
 *   - apiBase="admin"    → POST /api/admin/gates/<id>/commands
 *
 * Lifecycle the user sees:
 *   1. Idle: a single big "פתח שער" or "סגור שער" button (depends on
 *      current ``isOpen``).
 *   2. Click → Portal confirmation dialog ("are you sure?"). Esc /
 *      backdrop / cancel return to idle.
 *   3. Confirm → issue the command (POST), enter a polling state that
 *      reloads the command's row every 1.5s.
 *   4. Terminal state: a coloured pill — green ✓ for acknowledged,
 *      orange ⏳ for timeout (no device on the other end yet —
 *      expected in Phase 3 because no real Shelly is listening), red
 *      ✗ for failed. After 5 s the pill fades and we return to idle.
 *
 * In Phase 4, when a Shelly actually receives commands, the polling
 * resolves to ``acknowledged`` quickly and the user sees ✓. No
 * frontend change required.
 *
 * Hidden entirely for viewers (read-only). The caller decides whether
 * to render this; we don't gate visibility here.
 */

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_DURATION_MS = 35000;  // covers the 30s server-side timeout + slack
const RESET_DELAY_MS = 5000;


function GateControlPanel({ gateId, isOpen, apiBase = 'customer', isDeviceOnline = true, onCommandComplete }) {
  // 'idle' | 'confirming' | 'sending' | 'polling' | 'done'
  const [phase, setPhase] = useState('idle');
  const [pendingType, setPendingType] = useState(null);
  const [terminal, setTerminal] = useState(null);  // { status, error? }
  const [error, setError] = useState(null);

  const requestType = isOpen ? 'close' : 'open';
  const requestLabel = isOpen ? 'סגור שער' : 'פתח שער';

  const start = (type) => {
    setPendingType(type);
    setPhase('confirming');
    setError(null);
    setTerminal(null);
  };

  const cancel = () => {
    setPhase('idle');
    setPendingType(null);
  };

  const send = async () => {
    setPhase('sending');
    try {
      const res = await axios.post(`/api/${apiBase}/gates/${gateId}/commands`, { type: pendingType });
      const cmd = res.data;
      setPhase('polling');
      pollUntilTerminal(cmd.id);
    } catch (err) {
      // 409 with reason="offline" means the backend rejected the
      // command up front because the device isn't currently
      // connected. Render that as a distinct "not connected" pill
      // rather than a generic "failed" so the user doesn't think
      // their click was processed at all.
      const status = err.response?.status === 409 ? 'offline' : 'failed';
      setPhase('done');
      setTerminal({
        status,
        error: err.response?.data?.error || 'שליחה נכשלה',
      });
      scheduleReset();
    }
  };

  const pollUntilTerminal = (commandId) => {
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const res = await axios.get(`/api/${apiBase}/gates/${gateId}/commands/${commandId}`);
        const cmd = res.data;
        if (['acknowledged', 'failed', 'timeout'].includes(cmd.status)) {
          setPhase('done');
          setTerminal({ status: cmd.status, error: cmd.error });
          scheduleReset();
          if (onCommandComplete) onCommandComplete(cmd);
          return;
        }
      } catch {
        // Network blip — keep polling silently. The polling timeout
        // below will eventually give up.
      }
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        // Stop polling on our side; the audit log will show whatever
        // status the server settles on.
        setPhase('done');
        setTerminal({ status: 'timeout', error: null });
        scheduleReset();
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    setTimeout(tick, POLL_INTERVAL_MS);
  };

  const scheduleReset = () => {
    setTimeout(() => {
      setPhase('idle');
      setPendingType(null);
      setTerminal(null);
    }, RESET_DELAY_MS);
  };

  // ---- Render ------------------------------------------------------------

  if (phase === 'sending' || phase === 'polling') {
    return (
      <div style={pillStyleNeutral}>
        <Spinner />
        <span>{phase === 'sending' ? 'שולח פקודה...' : 'ממתין לאישור...'}</span>
      </div>
    );
  }

  if (phase === 'done' && terminal) {
    if (terminal.status === 'acknowledged') {
      return <div style={pillStyleSuccess}>✓ הפקודה בוצעה</div>;
    }
    if (terminal.status === 'offline') {
      return (
        <div style={pillStyleWarning}>
          ⚠ {terminal.error || 'הרכיב לא מחובר'}
        </div>
      );
    }
    if (terminal.status === 'timeout') {
      return (
        <div style={pillStyleWarning}>
          ⏳ פג תוקף — הרכיב לא אישר את הפקודה
        </div>
      );
    }
    return (
      <div style={pillStyleError}>
        ✗ {terminal.error || 'הפקודה נכשלה'}
      </div>
    );
  }

  return (
    <>
      {/* When the Shelly is offline we render a clearly-labelled
          "disconnected" banner ABOVE the disabled button so the user
          immediately understands why nothing happens on click and what
          to do about it (wait — system auto-enables when device returns). */}
      {!isDeviceOnline && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.55rem',
          padding: '0.7rem 0.85rem',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '6px',
          color: '#9a3412',
          marginBottom: '0.6rem',
          fontSize: '0.85rem',
        }}>
          <span style={{ fontSize: '1.05rem', lineHeight: 1, flexShrink: 0 }} aria-hidden="true">🔌</span>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.15rem' }}>
              הרכיב מנותק כרגע
            </div>
            <div style={{ color: '#9a3412', opacity: 0.85, lineHeight: 1.45 }}>
              כשהרכיב יחזור להתחבר, הכפתור יופעל אוטומטית.
              אם הרכיב מותקן ולא חוזר — פנו לצוות התמיכה.
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => isDeviceOnline && start(requestType)}
        disabled={!isDeviceOnline}
        title={isDeviceOnline ? '' : 'הרכיב לא מחובר — לא ניתן לשלוח פקודה כרגע'}
        style={{
          width: '100%',
          padding: '0.85rem 1rem',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.95rem',
          fontWeight: 600,
          fontFamily: 'Heebo, sans-serif',
          color: '#ffffff',
          cursor: isDeviceOnline ? 'pointer' : 'not-allowed',
          background: !isDeviceOnline
            ? '#9ca3af'
            : (requestType === 'open' ? '#dc2626' : '#1a1a2e'),
          marginBottom: '0.85rem',
          opacity: isDeviceOnline ? 1 : 0.6,
        }}
      >
        {requestLabel}
      </button>

      {phase === 'confirming' && createPortal(
        <ConfirmDialog
          requestType={pendingType}
          onCancel={cancel}
          onConfirm={send}
        />,
        document.body
      )}

      {error && (
        <div style={{ ...pillStyleError, marginBottom: '0.5rem' }}>{error}</div>
      )}
    </>
  );
}


function ConfirmDialog({ requestType, onCancel, onConfirm }) {
  const heading = requestType === 'open' ? 'אישור פתיחת שער' : 'אישור סגירת שער';
  const message = requestType === 'open'
    ? 'האם אתה בטוח שברצונך לפתוח את השער? הפקודה תישלח מיידית לרכיב.'
    : 'האם אתה בטוח שברצונך לסגור את השער? הפקודה תישלח מיידית לרכיב.';
  const confirmLabel = requestType === 'open' ? 'כן, פתח' : 'כן, סגור';
  const confirmColor = requestType === 'open' ? '#dc2626' : '#1a1a2e';

  return (
    <>
      <div className="modal-overlay" onClick={onCancel} />
      <div
        className="modal"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(420px, calc(100vw - 24px))',
          padding: '1.5rem',
        }}
      >
        <h2 style={{ marginBottom: '0.6rem', fontSize: '1.05rem', fontWeight: 600 }}>{heading}</h2>
        <p style={{ color: '#4a5568', fontSize: '0.9rem', marginBottom: '1.25rem' }}>{message}</p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '5px',
              fontSize: '0.85rem',
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
              color: '#4a5568',
            }}
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            style={{
              padding: '0.5rem 1.1rem',
              background: confirmColor,
              border: 'none',
              borderRadius: '5px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
              color: '#ffffff',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}


function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: '14px',
        height: '14px',
        border: '2px solid #e5e7eb',
        borderTopColor: '#1a1a2e',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  );
}


// Inline keyframes so we don't have to coordinate with App.css for one
// component. React will dedupe identical text nodes.
function SpinnerStyle() {
  return (
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  );
}


const pillStyleBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.7rem 1rem',
  borderRadius: '6px',
  fontSize: '0.9rem',
  fontWeight: 500,
  marginBottom: '0.85rem',
  fontFamily: 'Heebo, sans-serif',
};
const pillStyleNeutral = { ...pillStyleBase, background: '#f3f4f6', color: '#1a1a2e', border: '1px solid #e5e7eb' };
const pillStyleSuccess = { ...pillStyleBase, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
const pillStyleWarning = { ...pillStyleBase, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' };
const pillStyleError = { ...pillStyleBase, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };


function GateControlPanelWithStyle(props) {
  return (
    <>
      <SpinnerStyle />
      <GateControlPanel {...props} />
    </>
  );
}


export default GateControlPanelWithStyle;
